/**
 * @voltxampere/engine — DC stamp context factory
 *
 * Encapsulates the bookkeeping that lets each {@link DCStampFn}
 * write into the global MNA system without owning a reference to the
 * sparse matrix. The factory hands back:
 *
 *  - a closure-bound `addY(i, j, v)` that pushes into a triplet
 *    buffer the MNA builder later passes to
 *    `createSparse(N+M, N+M, triplets)` (Sprint 4's CSR primitive
 *    sums duplicates, so the builder doesn't deduplicate);
 *  - the live `rhs` typed-array, which stamps mutate in place;
 *  - bound `nodeIndex` and `vSourceIndex` lookups that consult
 *    the maps the builder constructed during the planning phase.
 *
 * Ground rows/cols are not present in the MNA system. The factory
 * surfaces this by returning `-1` from `nodeIndex(groundNetId)` and
 * by silently dropping `addY(i, j, v)` calls where `i === -1` or
 * `j === -1`. Stamps therefore stay branch-free with respect to
 * "is this terminal grounded?" — a leg on GND just gets compressed
 * out of the matrix, which is the textbook behaviour.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { REFERENCE_TEMPERATURE_K } from "./nonlinear/physical-constants.js";
/**
 * Build the {@link DCStampContext} that gets handed to each stamp
 * during the per-iteration MNA assembly. The context closures over
 * the triplet buffer + rhs + maps; mutating those after the fact is
 * the builder's responsibility.
 */
export function createDCStampContext(deps) {
    const internalNodeCount = deps.internalNodeCount ?? 0;
    const size = deps.nodeCount + internalNodeCount + deps.vSourceCount;
    if (deps.rhs.length !== size) {
        throw new ContractViolationError(`createDCStampContext: rhs length ${String(deps.rhs.length)} ≠ N+I+M ${String(size)}`);
    }
    if (deps.xPrev.length !== size) {
        throw new ContractViolationError(`createDCStampContext: xPrev length ${String(deps.xPrev.length)} ≠ N+I+M ${String(size)}`);
    }
    function nodeIndex(netId) {
        if (netId === deps.groundNetId)
            return -1;
        const idx = deps.netToIndex.get(netId);
        if (idx === undefined) {
            throw new ContractViolationError(`nodeIndex: net "${netId}" is not part of the MNA index (planner bug)`);
        }
        return idx;
    }
    function vSourceIndex(componentId) {
        const idx = deps.vSourceIdToIndex.get(componentId);
        if (idx === undefined) {
            throw new ContractViolationError(`vSourceIndex: component "${componentId}" is not registered as a voltage source (planner bug)`);
        }
        // Sprint 24 B1 — vsource rows live AFTER internal nodes.
        return deps.nodeCount + internalNodeCount + idx;
    }
    function internalNodeIndex(componentId, slotIndex) {
        const map = deps.internalNodeIndices;
        if (map === undefined) {
            throw new ContractViolationError(`internalNodeIndex: no internal node allocation map provided (planner bug)`);
        }
        const slots = map.get(componentId);
        if (slots === undefined) {
            throw new ContractViolationError(`internalNodeIndex: component "${componentId}" did not allocate any internal nodes (stamp dynamicAugments() returned no "internalNode" entries)`);
        }
        if (slotIndex < 0 || slotIndex >= slots.length) {
            throw new ContractViolationError(`internalNodeIndex: component "${componentId}" slotIndex ${String(slotIndex)} out of range [0, ${String(slots.length)})`);
        }
        const idx = slots[slotIndex];
        if (idx === undefined) {
            // Defensive — covered by the bounds check above but the type
            // system reads `slots` as `readonly number[]` so [n] is `number | undefined`.
            throw new ContractViolationError(`internalNodeIndex: component "${componentId}" slot ${String(slotIndex)} index is undefined`);
        }
        return idx;
    }
    function addY(i, j, v) {
        // Ground row/col compression — see file-level docstring.
        if (i < 0 || j < 0)
            return;
        if (v === 0)
            return;
        deps.triplets.push({ row: i, col: j, value: v });
    }
    return {
        size,
        nodeCount: deps.nodeCount,
        nodeIndex,
        vSourceIndex,
        internalNodeIndex,
        addY,
        rhs: deps.rhs,
        xPrev: deps.xPrev,
        t: deps.t,
        gmin: deps.gmin,
        sourceScale: deps.sourceScale,
        junctionHistory: deps.junctionHistory ?? new Map(),
        enableDeltaVDamping: deps.enableDeltaVDamping ?? true,
        temperature: deps.temperature ?? REFERENCE_TEMPERATURE_K,
    };
}
//# sourceMappingURL=context.js.map